import {
  CheckDifferentConversationDatePipe,
  CheckDifferentConversationMemberPipe,
  DialogFormComponent,
  DialogService,
  DownloadService,
  DownloadStatus,
  ExportComponentId,
  MessageHiddenTimePipe,
  ThemeService,
  UiHelpersService,
  UiModule,
} from '@shared/ui';
import { ConversationViewOptions } from 'apps/phoenix/src/app/shared/ui/components/button-toggle/button-toggle.component';
import { scrollToFirstHighlight } from 'apps/phoenix/src/app/shared/ui/utilities/highlight.utils';
import { PhoenixTrpcClient, RouterInput } from 'apps/phoenix/src/app/utils';
import { marked } from 'marked';
import randomColor from 'randomcolor';
import { Subject, from, interval, takeUntil } from 'rxjs';

import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  TemplateRef,
  ViewChild,
  effect,
  inject,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';

import { FeatureFlagsService } from '../../../../shared/services';
import { DirectCollectService } from '../../services/direct-collect.service';
import { MessageAttachmentComponent } from '../message-attachment/message-attachment.component';
import { TimelineDetailMessageComponent } from '../timeline-detail-message/timeline-detail-message.component';

@Component({
  selector: 'phoenix-timeline-messages-detail',
  imports: [
    UiModule,
    MessageAttachmentComponent,
    TimelineDetailMessageComponent,
    CheckDifferentConversationDatePipe,
    CheckDifferentConversationMemberPipe,
    MessageHiddenTimePipe,
  ],
  templateUrl: './timeline-detail-messages.component.html',
  styleUrls: ['./timeline-detail-messages.component.scss'],
})
export class TimelineDetailMessagesComponent implements OnInit, OnDestroy {
  @ViewChild(CdkVirtualScrollViewport) viewport!: CdkVirtualScrollViewport;
  @ViewChild('highlightContainer') highlightContainerRef!: ElementRef;
  @ViewChild('largeExportDialogForm')
  largeExportDialogForm!: TemplateRef<unknown>;

  // Threshold for large export warning
  readonly LARGE_EXPORT_THRESHOLD = 100_000;

  _selectedConversation!: any;
  @Input() set selectedConversation(value: any) {
    if (value) {
      this._selectedConversation = value;
      this?.viewport?.scrollToIndex(0);
      this.messageCollection = [];

      this.setActorsColors();

      this.firstConversation = true;
      this.conversationAfter = '';
      this.conversationBefore = '';

      this.loadConversation();
    }
  }
  @ViewChild('templateMessage')
  messageComponent!: TemplateRef<any>;
  selectedMessage!: any;

  messageCollection: any[] = [];
  actorsColors: any = {};
  lineLimit = 100;
  showLines = 50;
  downloadComponentId: ExportComponentId =
    'download-management-direct-collection-conversation';

  loadingConversations = 0;
  firstConversation = true;
  isScrollActive = false;
  destroy$ = new Subject();

  confirmLargeExportForm = new FormGroup({
    confirmLargeExport: new FormControl(false),
  });

  private largeExportDialogRef!: MatDialogRef<DialogFormComponent>;

  trpcService = inject(PhoenixTrpcClient);
  directService = inject(DirectCollectService);
  uiService = inject(UiHelpersService);
  dialogService = inject(DialogService);
  themeService = inject(ThemeService);
  renderer = inject(Renderer2);
  route = inject(ActivatedRoute);
  featureFlagsService = inject(FeatureFlagsService);
  downloadService = inject(DownloadService);

  options!: ConversationViewOptions[];
  selectedViewOption = 'plainText';

  conversationAfter = '';
  conversationBefore = '';

  showHighlightButton = false;

  constructor() {
    effect(() => {
      this.setActorsColors();
    });
  }

  get userHasPermissionToExport(): boolean {
    return this.featureFlagsService.isOn('allow_direct_collect_export');
  }

  ngOnInit(): void {
    this.options = [
      { text: 'Plain Text', value: 'plainText', tooltip: 'Plain Text view' },
      { text: 'Markdown', value: 'markdown', tooltip: 'Markdown view' },
      { text: 'HTML', value: 'html', tooltip: 'HTML view' },
    ];

    interval(1000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.checkScrollActive();
      });
  }

  get conversationInfo() {
    return this._selectedConversation?.conversation;
  }

  isMessageCorrupted(msg: any): boolean {
    return msg?.corrupted === true;
  }

  setActorsColors() {
    this.actorsColors = {};
    const colors = randomColor({
      luminosity: this.themeService.themeSignal() === 'dark' ? 'light' : 'dark',
      count: this._selectedConversation?.conversation?.members?.length ?? 0,
    });
    (this._selectedConversation?.conversation?.members ?? []).forEach(
      (member: string, i: number) => {
        this.actorsColors[member.toLowerCase()] = colors[i];
      }
    );
  }

  checkScrollActive() {
    this.isScrollActive = this.uiService.checkScrollActive(
      this.viewport?.elementRef?.nativeElement
    );
  }

  loadConversation(before = false) {
    this.directService.itemLoading = true;
    this.loadingConversations++;

    const query = {
      conversation_id: this.conversationInfo?.conversation_id,
      start_date: this.conversationInfo?.start_date,
      type: this.conversationInfo?.type,
      channel_type: this.conversationInfo?.channel_type,
      is_channel: this.conversationInfo?.is_channel,
      ...(this.conversationInfo?.end_date && {
        end_date: this.conversationInfo.end_date,
      }),
      ...(this.conversationInfo?.name && {
        name: this.conversationInfo.name,
      }),
      highlightSearchTerm: this.directService.usedFilters$()?.query ?? '',
      take: 10,
    };

    if (before) {
      this.conversationBefore &&
        this.conversationBefore !== '' &&
        (query.before = this.conversationBefore);
    } else {
      this.conversationAfter &&
        this.conversationAfter !== '' &&
        (query.after = this.conversationAfter);
    }

    from(this.trpcService.directCollect.getConversation.query(query))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: data => {
          const hasHighlight =
            data?.edges?.some(edge => {
              const highlight = edge?.highlight;
              return highlight && Object.keys(highlight).length > 0;
            }) ?? false;
          this.showHighlightButton = hasHighlight;
          if (this.firstConversation) {
            data?.page_info?.after &&
              (this.conversationAfter = data.page_info.after);
            data?.page_info?.before &&
              (this.conversationBefore = data.page_info.before);
          } else if (before) {
            this.conversationBefore = data?.page_info?.before ?? '';
          } else {
            this.conversationAfter = data?.page_info?.after ?? '';
          }

          if (before) {
            this.messageCollection = [
              ...(data?.edges?.map((e: any) =>
                this.uiService.formatResponse(e)
              ) ?? []),
              ...this.messageCollection,
            ];
          } else {
            this.messageCollection.push(
              ...(data?.edges?.map((e: any) =>
                this.uiService.formatResponse(e)
              ) ?? [])
            );
          }
          setTimeout(() => {
            this.loadingConversations--;
            if (this.loadingConversations === 0) {
              this.directService.itemLoading = false;
            }
          }, 1000);

          this.firstConversation = false;
        },
        error: error => {
          this.loadingConversations--;
          this.directService.itemLoading = false;
          this.uiService.handleErrorMessage(error, 'Error loading messages!');
        },
      });
  }

  hasTranslations(messageCollection: any[]): boolean {
    return messageCollection.some(message => message.translation);
  }

  truncateMessage(message: string): string | Promise<string> {
    if (!message) return '';
    switch (this.selectedViewOption) {
      case 'html':
        return this.uiService.truncateMessage(message);
      case 'markdown':
        return marked(this.uiService.truncateMessage(message));
      case 'plainText':
      default:
        return this.uiService.truncateMessage(this.escapeHtml(message));
    }
  }

  escapeHtml(html: string): string {
    const div = document.createElement('div');
    div.textContent = html;
    return div.innerHTML;
  }

  openDialog(msg: any) {
    if (!this.directService.itemLoading$()) {
      this.selectedMessage = msg;
      this.dialogService.info(this.messageComponent, {
        width: '80%',
      });
    }
  }

  onScroll(event: any): void {
    if (
      event.target.offsetHeight + event.target.scrollTop >=
        event.target.scrollHeight * 0.85 &&
      !this.directService.itemLoading$() &&
      this.conversationAfter !== ''
    ) {
      this.directService.itemLoading = true;
      this.loadConversation();
    }
  }

  loadBefore() {
    this.loadConversation(true);
  }

  getFormattedThreshold(): string {
    return this.LARGE_EXPORT_THRESHOLD.toLocaleString();
  }

  shouldShowLargeExportWarning(): boolean {
    const total = this.conversationInfo?.total ?? 0;
    return total > this.LARGE_EXPORT_THRESHOLD;
  }

  displayLargeExportWarning() {
    // Reset and setup form validation
    const confirmControl = this.confirmLargeExportForm.get(
      'confirmLargeExport'
    ) as FormControl;
    confirmControl.setValue(false);
    confirmControl.setValidators([Validators.requiredTrue]);
    confirmControl.updateValueAndValidity();

    const dialogConfig = {
      width: '450px',
      data: {
        btnLabel: 'Download',
      },
    };

    this.largeExportDialogRef = this.dialogService.form(
      this.largeExportDialogForm,
      this.confirmLargeExportForm,
      dialogConfig
    );

    const dialogComponent = this.largeExportDialogRef?.componentInstance;
    if (!dialogComponent?.submitButton) {
      return;
    }

    dialogComponent.submitButton
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.proceedWithDownload();
      });
  }

  proceedWithDownload() {
    this.largeExportDialogRef?.close();
    this.performDownload();
  }

  performDownload() {
    if (!this.conversationInfo) {
      return;
    }

    const filters: RouterInput['directCollect']['exportConversationAsync'] = {
      start_date: this.conversationInfo.start_date ?? '',
      conversation_id: this.conversationInfo.conversation_id ?? '',

      type: this.conversationInfo.type ?? '',
      channel_type: this.conversationInfo.channel_type ?? '',
      name: this.conversationInfo.name ?? '',
      is_channel: this.conversationInfo.is_channel ?? false,
      take: 50,
    };

    this.conversationInfo.end_date &&
      (filters.end_date = this.conversationInfo.end_date);
    this.conversationInfo.name && (filters.name = this.conversationInfo.name);

    from(this.trpcService.directCollect.exportConversationAsync.query(filters))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: result => {
          this.downloadService.registerDownload({
            ...result,
            componentId: this.downloadComponentId,
          } as DownloadStatus);
        },
        error: error => {
          this.uiService.handleErrorMessage(
            error,
            'Failed to download conversation'
          );
        },
      });
  }

  downloadConversationSync() {
    // Check if conversation exceeds threshold and show warning if needed
    if (this.shouldShowLargeExportWarning()) {
      this.displayLargeExportWarning();
    } else {
      this.proceedWithDownload();
    }
  }

  scrollToFirstHighlight = () => {
    const container = this.highlightContainerRef?.nativeElement;
    if (!container) return;

    scrollToFirstHighlight(container);
  };

  changeViewOption(value: string) {
    this.selectedViewOption = value;
  }

  ngOnDestroy(): void {
    this.uiService.snackBar.dismiss();
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
